var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable max-lines-per-function */
import React, { useRef, useImperativeHandle, useEffect, forwardRef } from 'react';
import CBForm from '@cb/apricot/CBForm';
import { useUID } from 'react-uid';
import cn from 'classnames';
export const Select = forwardRef((_a, ref) => {
    var { selectId, validationMessageId, name, values, labelNoValue, defaultValue, mainClassName, label, labelId, labelClassName, truncate = true, ariaLabel, ariaDescribedby, floating, floatingBg, condensed, required, disabled, reset, validation, validationMsg, validationMsgRole, helperClassName, ariaLive, onChange, onFocus, onBlur, value, cbRef, 
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    css = '' } = _a, props = __rest(_a, ["selectId", "validationMessageId", "name", "values", "labelNoValue", "defaultValue", "mainClassName", "label", "labelId", "labelClassName", "truncate", "ariaLabel", "ariaDescribedby", "floating", "floatingBg", "condensed", "required", "disabled", "reset", "validation", "validationMsg", "validationMsgRole", "helperClassName", "ariaLive", "onChange", "onFocus", "onBlur", "value", "cbRef", "css"]);
    let apricotSelect;
    const selectRef = useRef(null);
    /**
     * Select changed - pass the update in the callback
     */
    const onSelectChange = () => {
        const elValue = selectRef.current ? selectRef.current.value : '';
        if (labelNoValue && elValue === '') {
            // GS-9131
            // selectElement.selectedIndex = -1;
            if (floating) {
                selectRef.current.selectedIndex = -1;
            }
            else {
                selectRef.current.selectedIndex = 0;
            }
            apricotSelect === null || apricotSelect === void 0 ? void 0 : apricotSelect.change();
        }
        onChange(elValue);
    };
    /**
     * Render Regular options for select box
     * @param options
     */
    const renderOptions = (options) => {
        return options.map((option, index) => {
            return (React.createElement("option", { key: index, value: option.value, disabled: option.disabled }, option.label));
        });
    };
    /**
     * Render grouped options for select box
     * @param groupedOptions
     */
    const renderGroupOptions = (groupedOptions) => {
        return groupedOptions.map((options, index) => {
            return (React.createElement("optgroup", { key: index, label: options.optgroup }, renderOptions(options.options)));
        });
    };
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    selectId = selectId || `apricot_select_${uid}`;
    // eslint-disable-next-line no-param-reassign
    validationMessageId = validationMessageId || `apricot_select_err_${uid}`;
    const labelClass = cn(required && 'cb-required', floating && floatingBg && `cb-${floatingBg}-bg`, labelClassName);
    const helperClass = cn('cb-input-helper', validation && `cb-validation-${validation}`, helperClassName);
    const getDescribedBy = () => {
        if (validation) {
            if (validationMsg && ariaDescribedby) {
                return `${ariaDescribedby} ${validationMessageId}`;
            }
            return validationMsg ? validationMessageId : ariaDescribedby;
        }
        return ariaDescribedby;
    };
    const describedbyValue = getDescribedBy() || null;
    const selectClass = cn('cb-select', disabled && 'cb-disabled', floating && 'cb-floating-label', condensed && 'cb-input-condensed', validation && `cb-validation-${validation}`, validation && 'display-block', mainClassName);
    useImperativeHandle(ref, () => selectRef.current);
    const isCalledRef = useRef(false);
    useEffect(() => {
        floating && CBForm.floatingLabel(selectRef.current);
        if (labelNoValue && !defaultValue && !value) {
            if (floating) {
                selectRef.current.selectedIndex = -1;
            }
            else {
                selectRef.current.selectedIndex = 0;
            }
            apricotSelect === null || apricotSelect === void 0 ? void 0 : apricotSelect.change();
        }
        if (cbRef) {
            // eslint-disable-next-line no-param-reassign
            cbRef.current = apricotSelect;
        }
        return () => {
            // We just want this to run once
            if (isCalledRef.current) {
                apricotSelect === null || apricotSelect === void 0 ? void 0 : apricotSelect.destroy();
            }
            else {
                isCalledRef.current = true;
            }
        };
    }, []);
    useEffect(() => {
        if (reset) {
            if (floating) {
                selectRef.current.selectedIndex = -1;
            }
            else {
                selectRef.current.selectedIndex = 0;
            }
            apricotSelect === null || apricotSelect === void 0 ? void 0 : apricotSelect.change();
        }
    }, [reset]);
    useEffect(() => {
        if (disabled) {
            const event = new CustomEvent('disable');
            selectRef.current.dispatchEvent(event);
        }
        else {
            const event = new CustomEvent('enable');
            selectRef.current.dispatchEvent(event);
        }
    }, [disabled]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: selectClass, "data-cbtrack-comp": "apricot-react:select" },
            label && (React.createElement("label", { htmlFor: selectId, className: labelClass, id: labelId }, label)),
            React.createElement("select", Object.assign({ ref: el => {
                    if (el) {
                        selectRef.current = el;
                        apricotSelect = CBForm.customSelectElement({
                            elem: el,
                            truncate,
                        });
                        apricotSelect === null || apricotSelect === void 0 ? void 0 : apricotSelect.change();
                    }
                }, id: selectId, name: name, "aria-label": ariaLabel, "aria-describedby": describedbyValue || undefined, "aria-invalid": validation === 'error', disabled: disabled, defaultValue: defaultValue, value: value, onChange: onSelectChange, onFocus: onFocus, onBlur: onBlur, "aria-required": required }, props),
                labelNoValue && React.createElement("option", { value: "" }, labelNoValue),
                (values === null || values === void 0 ? void 0 : values.some((item) => item.hasOwnProperty('optgroup')))
                    ? renderGroupOptions(values)
                    : renderOptions(values))),
        validation && validationMsg && (React.createElement("p", { id: validationMessageId, className: helperClass, "aria-live": ariaLive, role: validationMsgRole }, validationMsg))));
});
//# sourceMappingURL=Select.js.map